import { Navigate, type RouteObject } from 'react-router-dom';
import { DevPrimitives } from '@/features/_dev/Primitives';
import { ScreenStub } from '@/features/_stub/ScreenStub';
import { Activity } from '@/features/activity/Activity';
import { Budget } from '@/features/budget/Budget';
import { BudgetPostDetail } from '@/features/budget/BudgetPostDetail';
import { InvoiceDetail } from '@/features/budget/InvoiceDetail';
import { Meerwerken } from '@/features/budget/Meerwerken';
import { Quotes } from '@/features/budget/Quotes';
import { ContactDetail } from '@/features/contacts/ContactDetail';
import { Contacts } from '@/features/contacts/Contacts';
import { Dashboard } from '@/features/dashboard/Dashboard';
import { Decisions } from '@/features/decisions/Decisions';
import { DocPreview } from '@/features/documents/DocPreview';
import { Documents } from '@/features/documents/Documents';
import { Plans } from '@/features/documents/Plans';
import { IA } from '@/features/ia/IA';
import { Keuringen } from '@/features/nazorg/Keuringen';
import { Warranties } from '@/features/nazorg/Warranties';
import { Onboarding } from '@/features/onboarding/Onboarding';
import { PhaseDetail } from '@/features/phase/PhaseDetail';
import { Settings } from '@/features/settings/Settings';
import { Timeline } from '@/features/timeline/Timeline';
import { Photos } from '@/features/werf/Photos';
import { Tasks } from '@/features/werf/Tasks';
import { Werfverslag } from '@/features/werf/Werfverslag';
import { WerfModus } from '@/features/werfmodus/WerfModus';
import { ProtectedRoute } from './ProtectedRoute';
import { SignInPage } from './SignInPage';
import { SignUpPage } from './SignUpPage';

/**
 * Route map. All 26 screens from the design handoff render real bodies
 * with inline mock data. The backend cut-over in Phase 6 swaps these
 * for TanStack Query reads against the .NET API.
 */

function Protected({ children }: { children: React.ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>;
}

export const routes: RouteObject[] = [
  // Auth (public)
  { path: '/sign-in/*', element: <SignInPage /> },
  { path: '/sign-up/*', element: <SignUpPage /> },

  // Project
  {
    path: '/',
    element: (
      <Protected>
        <Dashboard />
      </Protected>
    ),
  },
  {
    path: '/tijdlijn',
    element: (
      <Protected>
        <Timeline variant="vertical" />
      </Protected>
    ),
  },
  {
    path: '/tijdlijn/gantt',
    element: (
      <Protected>
        <Timeline variant="gantt" />
      </Protected>
    ),
  },
  {
    path: '/fases',
    element: (
      <Protected>
        <Navigate to="/fases/ruw" replace />
      </Protected>
    ),
  },
  {
    path: '/fases/:phaseId',
    element: (
      <Protected>
        <PhaseDetail />
      </Protected>
    ),
  },
  {
    path: '/documenten',
    element: (
      <Protected>
        <Documents />
      </Protected>
    ),
  },
  {
    path: '/documenten/:id',
    element: (
      <Protected>
        <DocPreview />
      </Protected>
    ),
  },
  {
    path: '/plannen',
    element: (
      <Protected>
        <Plans />
      </Protected>
    ),
  },
  {
    path: '/contacten',
    element: (
      <Protected>
        <Contacts />
      </Protected>
    ),
  },
  {
    path: '/contacten/:id',
    element: (
      <Protected>
        <ContactDetail />
      </Protected>
    ),
  },

  // Werk
  {
    path: '/budget',
    element: (
      <Protected>
        <Budget />
      </Protected>
    ),
  },
  {
    path: '/budget/factuur/:id',
    element: (
      <Protected>
        <InvoiceDetail />
      </Protected>
    ),
  },
  {
    path: '/budget/post/:postSlug',
    element: (
      <Protected>
        <BudgetPostDetail />
      </Protected>
    ),
  },
  {
    path: '/meerwerken',
    element: (
      <Protected>
        <Meerwerken />
      </Protected>
    ),
  },
  {
    path: '/offertes',
    element: (
      <Protected>
        <Quotes />
      </Protected>
    ),
  },
  {
    path: '/beslissingen',
    element: (
      <Protected>
        <Decisions />
      </Protected>
    ),
  },
  {
    path: '/taken',
    element: (
      <Protected>
        <Tasks />
      </Protected>
    ),
  },
  {
    path: '/fotos',
    element: (
      <Protected>
        <Photos />
      </Protected>
    ),
  },
  {
    path: '/werfverslagen',
    element: (
      <Protected>
        <Werfverslag />
      </Protected>
    ),
  },
  {
    path: '/keuringen',
    element: (
      <Protected>
        <Keuringen />
      </Protected>
    ),
  },
  {
    path: '/garanties',
    element: (
      <Protected>
        <Warranties />
      </Protected>
    ),
  },
  {
    path: '/onboarding',
    element: (
      <Protected>
        <Onboarding />
      </Protected>
    ),
  },
  {
    path: '/werf-modus',
    element: (
      <Protected>
        <WerfModus />
      </Protected>
    ),
  },

  // Onbeperkt
  {
    path: '/instellingen',
    element: (
      <Protected>
        <Settings />
      </Protected>
    ),
  },
  {
    path: '/activiteit',
    element: (
      <Protected>
        <Activity />
      </Protected>
    ),
  },
  {
    path: '/ia',
    element: (
      <Protected>
        <IA />
      </Protected>
    ),
  },

  // Internal: design-system showcase (dev-only review surface for F0 primitives).
  {
    path: '/dev/primitives',
    element: (
      <Protected>
        <DevPrimitives />
      </Protected>
    ),
  },

  // 404
  {
    path: '*',
    element: <ScreenStub title="Pagina niet gevonden" eyebrow="404" />,
  },
];
